import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { ObjectKey } from 'utils/tsHelpers';
import { z } from 'zod';
import { WebhookAuthType, WebhookFormField } from '../../types';
import { getDefaultDestinationsFormValues } from './DestinationsFormProvider.utils';

const BaseDestinationsSchema = z.object({
  name: z.string().trim().min(1, 'Webhook name is required'),
  endpoint: z.string().min(1, 'Endpoint URL is required').url(),
});

const schema = z.discriminatedUnion(WebhookFormField.AuthType, [
  BaseDestinationsSchema.extend({
    authType: z.literal(WebhookAuthType.NONE),
  }),
  BaseDestinationsSchema.extend({
    authType: z.literal(WebhookAuthType.BASIC),
    authToken: z.string().min(1, 'Authentication token is required'),
  }),
  BaseDestinationsSchema.extend({
    authType: z.literal(WebhookAuthType.OAUTH2),
    clientId: z.string().min(1, 'Client ID is required'),
    clientSecret: z.string().min(1, 'Client secret is required'),
    tokenEndpoint: z.string().min(1, 'Token URL is required').url(),
  }),
]);

export type DestinationsFormValues = z.infer<typeof schema>;

export const DestinationsFormProvider = ({ children }: React.PropsWithChildren<unknown>) => {
  const defaultValues = getDefaultDestinationsFormValues();

  const methods = useForm<DestinationsFormValues>({
    defaultValues,
    mode: 'onChange',
    resolver: zodResolver(schema),
    shouldUnregister: true,
  });

  return <FormProvider {...methods}>{children}</FormProvider>;
};

export const withDestinationsFormProvider =
  <DestinationsFormConsumerProps extends Record<ObjectKey, unknown>>(
    DestinationsFormConsumer: React.FC<DestinationsFormConsumerProps>,
  ) =>
  (props: DestinationsFormConsumerProps) => (
    <DestinationsFormProvider>
      <DestinationsFormConsumer {...props} />
    </DestinationsFormProvider>
  );
